import { uploadToS3, clearS3ResumeState } from './awsS3UploadService';
import { applyMatchFootageUrls, markFootageStitchQueued } from './applyMatchFootageUrls';
import { matchFootagePartKey, triggerFootageStitch } from './footageStitch';
import { isStitchFootageLayout } from './dualMp4Footage';
import { createVideoThumbnail } from './uploadProgressFormat';

const MAX_JOB_RETRIES = 8;
const COMPLETE_DISMISS_MS = 8000;
const SPEED_SAMPLE_MS = 800;

let jobs = [];
const listeners = new Set();
let processing = false;

function emit() {
  const snapshot = jobs.map(publicJob);
  listeners.forEach((listener) => {
    try {
      listener(snapshot);
    } catch {
      /* ignore subscriber errors */
    }
  });
}

function publicFile(fileState) {
  return {
    id: fileState.id,
    name: fileState.name,
    size: fileState.size,
    loaded: fileState.loaded,
    status: fileState.status,
    compressPercent: fileState.compressPercent || 0,
    thumbnailUrl: fileState.thumbnailUrl,
    role: fileState.role,
    error: fileState.error || null,
  };
}

function publicJob(job) {
  const bytesUploaded = job.files.reduce((sum, file) => sum + (file.loaded || 0), 0);
  const bytesTotal = job.files.reduce((sum, file) => sum + (file.size || 0), 0);
  const percent = bytesTotal ? Math.min(100, Math.round((bytesUploaded / bytesTotal) * 100)) : 0;
  return {
    id: job.id,
    status: job.status,
    locationLabel: job.locationLabel,
    href: job.href,
    source: job.source,
    gameMatchIds: job.gameMatchIds,
    error: job.error || null,
    bytesUploaded,
    bytesTotal,
    percent: job.status === 'complete' ? 100 : percent,
    etaSeconds: job.etaSeconds,
    files: job.files.map(publicFile),
    createdAt: job.createdAt,
    stitchPending: job.mode === 'stitch',
    mode: job.mode || 'upload',
  };
}

function updateJob(jobId, patch) {
  jobs = jobs.map((job) => (job.id === jobId ? { ...job, ...patch } : job));
  emit();
}

function updateFile(jobId, fileId, patch) {
  jobs = jobs.map((job) => {
    if (job.id !== jobId) return job;
    return {
      ...job,
      files: job.files.map((file) => (file.id === fileId ? { ...file, ...patch } : file)),
    };
  });
  emit();
}

function newId(prefix) {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function hasActiveUploads() {
  return jobs.some((job) => ['queued', 'compressing', 'uploading', 'completing'].includes(job.status));
}

function getJob(jobId) {
  return jobs.find((job) => job.id === jobId) || null;
}

function estimateEta(job, loaded, total) {
  const now = Date.now();
  const samples = job.speedSamples || [];
  samples.push({ t: now, loaded });
  while (samples.length > 2 && now - samples[0].t > 12000) {
    samples.shift();
  }
  job.speedSamples = samples;
  if (samples.length < 2 || total <= loaded) return null;
  const first = samples[0];
  const last = samples[samples.length - 1];
  const dt = (last.t - first.t) / 1000;
  const dBytes = last.loaded - first.loaded;
  if (dt < 1 || dBytes <= 0) return job.etaSeconds ?? null;
  const bps = dBytes / dt;
  return Math.max(1, Math.round((total - loaded) / bps));
}

async function runFileUpload(job, fileState) {
  updateFile(job.id, fileState.id, { status: 'uploading', error: null, compressPercent: 0 });
  const controller = new AbortController();
  job.controllers[fileState.id] = controller;

  let lastEmit = 0;
  const uploadOptions = { signal: controller.signal };
  if (job.mode === 'stitch' && Number.isInteger(fileState.partIndex)) {
    uploadOptions.filePath = matchFootagePartKey(job.s3MatchId, fileState.partIndex);
  }

  const result = await uploadToS3(
    fileState.file,
    job.s3MatchId,
    (percent, details) => {
      if (details?.phase === 'compressing') {
        const compressPercent = details.phasePercent ?? percent;
        updateFile(job.id, fileState.id, { status: 'compressing', compressPercent, loaded: 0 });
        updateJob(job.id, { status: 'compressing', etaSeconds: null });
        return;
      }
      const total = details?.total || fileState.size;
      if (total && total !== fileState.size) {
        fileState.size = total;
      }
      const loaded = details?.loaded ?? Math.round(((percent || 0) / 100) * (fileState.size || total || 0));
      const now = Date.now();
      if (now - lastEmit < SPEED_SAMPLE_MS && loaded < fileState.size) return;
      lastEmit = now;
      const etaSeconds = estimateEta(job, job.files.reduce((sum, f) => (
        sum + (f.id === fileState.id ? loaded : (f.loaded || 0))
      ), 0), job.files.reduce((sum, f) => sum + (f.size || 0), 0));
      updateFile(job.id, fileState.id, { loaded, size: fileState.size, status: 'uploading' });
      updateJob(job.id, { status: 'uploading', etaSeconds });
    },
    job.allowAnonymous,
    { ...uploadOptions, uploadToken: job.uploadToken },
  );

  updateFile(job.id, fileState.id, {
    loaded: fileState.size,
    status: 'complete',
    url: result.videoUrl,
  });
  delete job.controllers[fileState.id];
  return result.videoUrl;
}

async function processJob(jobId) {
  const initial = getJob(jobId);
  if (!initial || initial.status === 'cancelled') return;
  updateJob(jobId, { status: 'uploading', error: null, etaSeconds: null });
  const live = getJob(jobId);
  if (live) live.speedSamples = [];

  try {
    let leftUrl = null;
    let rightUrl = null;
    const start = getJob(jobId);
    for (const fileState of start.files) {
      const current = getJob(jobId);
      if (!current || current.status === 'cancelled') return;
      if (fileState.status === 'complete' && fileState.url) {
        if (fileState.role === 'right') rightUrl = fileState.url;
        else leftUrl = fileState.url;
        continue;
      }
      const url = await runFileUpload(current, fileState);
      if (fileState.role === 'right') rightUrl = url;
      else leftUrl = url;
    }

    const attached = getJob(jobId);
    if (!attached || attached.status === 'cancelled') return;
    updateJob(jobId, { status: 'completing', etaSeconds: 0 });
    if (attached.mode === 'stitch') {
      try {
        await markFootageStitchQueued({
          matchIds: attached.gameMatchIds,
          partCount: attached.files.length,
        });
      } catch (markErr) {
        console.warn('Could not mark footage stitch pending:', markErr);
      }
      await triggerFootageStitch({
        matchId: attached.gameMatchIds[0],
        matchIds: attached.gameMatchIds,
        s3MatchKey: attached.s3MatchId,
        uploadToken: attached.uploadToken,
      });
    } else {
      await applyMatchFootageUrls({
        matchIds: attached.gameMatchIds,
        leftUrl,
        rightUrl,
        source: attached.source,
        uploadToken: attached.uploadToken,
      });
    }

    getJob(jobId)?.files.forEach((fileState) => clearS3ResumeState(fileState.file));
    updateJob(jobId, { status: 'complete', error: null, etaSeconds: 0 });
    getJob(jobId)?.files.forEach((fileState) => {
      updateFile(jobId, fileState.id, { loaded: fileState.size, status: 'complete' });
    });

    window.setTimeout(() => {
      const current = getJob(jobId);
      if (current?.status === 'complete') dismissJob(jobId);
    }, COMPLETE_DISMISS_MS);
  } catch (err) {
    const current = getJob(jobId);
    if (String(err?.message || '') === 'Upload was aborted' || current?.status === 'cancelled') {
      updateJob(jobId, { status: 'cancelled', error: null });
      return;
    }

    const attempt = (current?.retryCount || 0) + 1;
    const message = err?.message || 'Upload failed. Please retry.';
    const statusCode = Number(err?.status || err?.context?.status || 0);
    const retryable = statusCode ? statusCode >= 500 : /network error|timed out|connection dropped|failed with status 5\d\d/i.test(message);
    if (current) current.retryCount = attempt;
    (current?.files || []).forEach((fileState) => {
      if (fileState.status === 'uploading') {
        updateFile(jobId, fileState.id, { status: 'error', error: message });
      }
    });

    if (retryable && attempt <= MAX_JOB_RETRIES && typeof navigator !== 'undefined' && navigator.onLine !== false) {
      updateJob(jobId, { status: 'queued', error: `Connection dropped — retrying (${attempt}/${MAX_JOB_RETRIES})…` });
      await new Promise((resolve) => setTimeout(resolve, Math.min(30000, 1500 * 2 ** (attempt - 1))));
      const latest = getJob(jobId);
      if (latest && latest.status === 'queued') {
        await processJob(jobId);
      }
      return;
    }

    updateJob(jobId, { status: 'error', error: message });
  }
}

async function processQueue() {
  if (processing) return;
  processing = true;
  try {
    while (true) {
      const next = jobs.find((job) => job.status === 'queued');
      if (!next) break;
      await processJob(next.id);
    }
  } finally {
    processing = false;
  }
}

export function subscribeBackgroundUploads(listener) {
  listeners.add(listener);
  listener(jobs.map(publicJob));
  return () => listeners.delete(listener);
}

export function getBackgroundUploadJobs() {
  return jobs.map(publicJob);
}

export function getActiveUploadForMatch(matchId) {
  if (!matchId) return null;
  return jobs
    .map(publicJob)
    .find((job) => (
      (job.gameMatchIds || []).includes(matchId)
      && !['complete', 'cancelled'].includes(job.status)
    )) || null;
}

export function hasActiveBackgroundUploads() {
  return hasActiveUploads();
}

export function enqueueFootageJob({
  leftFile,
  rightFile = null,
  stitchFiles = null,
  s3MatchId,
  gameMatchIds = [],
  locationLabel = 'Match library',
  href = null,
  source = 'portal',
  allowAnonymous = false,
  uploadToken = null,
} = {}) {
  const stitchList = Array.isArray(stitchFiles) ? stitchFiles.filter(Boolean) : [];
  const stitchMode = stitchList.length >= 2;
  const sourceFiles = stitchMode ? stitchList : [leftFile, rightFile].filter(Boolean);
  const files = sourceFiles.map((file, index) => ({
    id: newId('file'),
    file,
    name: file.name,
    size: file.size,
    loaded: 0,
    status: 'queued',
    thumbnailUrl: null,
    role: stitchMode
      ? `part-${index + 1}`
      : (rightFile && index === 1 ? 'right' : (rightFile ? 'left' : 'single')),
    partIndex: stitchMode ? index : null,
    url: null,
    error: null,
  }));

  if (files.length === 0) {
    throw new Error('Please upload match footage.');
  }

  const job = {
    id: newId('upload'),
    status: 'queued',
    mode: stitchMode ? 'stitch' : 'upload',
    locationLabel,
    href,
    source,
    allowAnonymous,
    uploadToken,
    s3MatchId: s3MatchId || gameMatchIds[0] || 'upload',
    gameMatchIds: Array.from(new Set((gameMatchIds || []).filter(Boolean))),
    files,
    error: null,
    etaSeconds: null,
    retryCount: 0,
    controllers: {},
    speedSamples: [],
    createdAt: Date.now(),
  };

  jobs = [...jobs, job];
  emit();

  files.forEach((fileState) => {
    createVideoThumbnail(fileState.file).then((thumbnailUrl) => {
      if (!thumbnailUrl) return;
      updateFile(job.id, fileState.id, { thumbnailUrl });
    });
  });

  processQueue();
  return job.id;
}

export function enqueueMatchFootageJob({
  footageLayout,
  footageFile,
  footageFileRight,
  stitchFiles,
  ...rest
} = {}) {
  if (isStitchFootageLayout(footageLayout) || (Array.isArray(stitchFiles) && stitchFiles.length >= 2)) {
    return enqueueFootageJob({
      ...rest,
      stitchFiles,
    });
  }
  return enqueueFootageJob({
    ...rest,
    leftFile: footageFile,
    rightFile: footageFileRight,
  });
}

export function retryJob(jobId) {
  const job = jobs.find((item) => item.id === jobId);
  if (!job) return;
  job.retryCount = 0;
  job.files = job.files.map((file) => (
    file.status === 'complete'
      ? file
      : { ...file, status: 'queued', error: null }
  ));
  updateJob(jobId, { status: 'queued', error: null });
  processQueue();
}

export function cancelJob(jobId) {
  const job = jobs.find((item) => item.id === jobId);
  if (!job) return;
  Object.values(job.controllers || {}).forEach((controller) => {
    try {
      controller.abort();
    } catch {
      /* ignore */
    }
  });
  job.files.forEach((fileState) => {
    clearS3ResumeState(fileState.file);
    updateFile(jobId, fileState.id, { status: 'cancelled' });
  });
  updateJob(jobId, { status: 'cancelled', error: null });
}

export function dismissJob(jobId) {
  jobs = jobs.filter((job) => job.id !== jobId);
  emit();
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (event) => {
    if (!hasActiveUploads()) return;
    event.preventDefault();
    event.returnValue = '';
  });
  window.addEventListener('online', () => {
    jobs
      .filter((job) => job.status === 'error')
      .forEach((job) => retryJob(job.id));
  });
}
